import React, { useMemo } from 'react'
import styled from 'styled-components'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import StaticMap from 'components/StaticMap'

const getAddressData = unit => unit?.addressData || unit?.address_data || null

const getMapQuery = (unit, addressData) => {
  const latitude = addressData?.latitude
  const longitude = addressData?.longitude

  if (latitude !== null && latitude !== undefined && longitude !== null && longitude !== undefined) {
    return `${latitude},${longitude}`
  }

  return ''
}

const UnitLocationMap = ({ unit }) => {
  const addressData = getAddressData(unit)
  const query = useMemo(() => getMapQuery(unit, addressData), [unit, addressData])

  if (!query) {
    return (
      <MapCard>
        <MapHeader>
          <MapTitle>
            <LocationOnOutlinedIcon />
            Location
          </MapTitle>
        </MapHeader>
        <AddressLine>
          {addressData?.address || 'Exact map location is not available for this unit yet.'}
          {addressData?.postcode ? `, ${addressData.postcode}` : ''}
        </AddressLine>
        <MapUnavailable>
          Sync the unit address data again to attach exact coordinates.
        </MapUnavailable>
      </MapCard>
    )
  }

  const encodedQuery = encodeURIComponent(query)
  const externalUrl = `https://www.google.com/maps/search/?api=1&query=${encodedQuery}`
  const address = addressData?.address || 'Exact unit location'
  const postcode = addressData?.postcode

  return (
    <MapCard>
      <MapHeader>
        <MapTitle>
          <LocationOnOutlinedIcon />
          Location
        </MapTitle>
        <OpenMapLink href={externalUrl} target='_blank' rel='noreferrer'>
          Open in Google Maps
          <OpenInNewIcon />
        </OpenMapLink>
      </MapHeader>
      <AddressLine>
        {address}{postcode ? `, ${postcode}` : ''}
      </AddressLine>
      <MapCanvas>
        <StaticMap
          lat={addressData?.latitude}
          lng={addressData?.longitude}
          height="100%"
          label={`Map for ${unit?.name || 'unit'}`}
        />
      </MapCanvas>
    </MapCard>
  )
}

export default UnitLocationMap

const MapCard = styled.div`
  border: 1px solid #bdebf1;
  border-radius: 6px;
  margin-top: 18px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 10px 24px rgba(12, 19, 43, 0.04);
`

const MapHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 18px 8px;
  background: #fff;
  border-bottom: 1px solid rgba(54, 190, 205, 0.16);

  @media (max-width: 640px) {
    align-items: flex-start;
    flex-direction: column;
  }
`

const MapTitle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors.haiti};
  font-size: 16px;
  font-weight: 700;

  svg {
    color: #36becd;
    font-size: 19px;
  }
`

const OpenMapLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #0f9fb0;
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;

  svg {
    font-size: 15px;
  }

  &:hover {
    color: #fd836b;
  }
`

const AddressLine = styled.div`
  padding: 10px 18px 12px;
  color: ${({ theme }) => theme.colors.grey[700]};
  font-size: 13px;
  border-left: 3px solid #fd836b;
`

const MapCanvas = styled.div`
  position: relative;
  overflow: hidden;
  height: 260px;

  @media (max-width: 640px) {
    height: 220px;
  }
`

const MapFrame = styled.iframe`
  display: block;
  width: 100%;
  height: calc(100% + 48px);
  border: 0;
  transform: translateY(-48px);

  @media (max-width: 640px) {
    height: calc(100% + 48px);
  }
`

const MapPin = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  width: 34px;
  height: 34px;
  transform: translate(-50%, -100%);
  border-radius: 50% 50% 50% 0;
  background: #36becd;
  border: 3px solid #ffffff;
  box-shadow: 0 8px 18px rgba(12, 19, 43, 0.24);
  pointer-events: none;
  rotate: -45deg;

  &::after {
    content: '';
    position: absolute;
    left: 22px;
    top: 22px;
    width: 18px;
    height: 8px;
    border-radius: 50%;
    background: rgba(12, 19, 43, 0.16);
    transform: rotate(45deg);
  }
`

const PinDot = styled.span`
  position: absolute;
  left: 50%;
  top: 50%;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #ffffff;
  transform: translate(-50%, -50%);
`

const MapUnavailable = styled.div`
  margin: 0 18px 18px;
  padding: 14px;
  border-radius: 6px;
  background: linear-gradient(90deg, rgba(54, 190, 205, 0.08), rgba(253, 131, 107, 0.08));
  border: 1px solid rgba(54, 190, 205, 0.18);
  color: ${({ theme }) => theme.colors.grey[700]};
  font-size: 13px;
`
